import { useMemo } from 'react'
import type { CalculateMetadataFunction } from 'remotion'
import {
  AllItemsContext,
  AssetsContext,
  ThemeContext,
  TracksContext,
} from './contexts'
import { docDurationInFrames } from './frames'
import { Layers } from './layers/Layers'
import { lowerScenes } from './lowerScenes'
import type { TimelineDoc } from './types'
import { Watermark } from './Watermark'

/**
 * The one composition for every v2 render (#77): the hydrated document in,
 * the Starter's layer tree out. The editor renders the same `<Layers>` with
 * its own context tree around them; Lambda renders this. Scenes (#566) are
 * lowered to the media items that play them here, not at hydration; the
 * length (`calculateTimelineMetadata`) is measured before, on the scenes.
 */
export function TimelineComposition(props: TimelineDoc) {
  const doc = useMemo(() => lowerScenes(props), [props])
  const tracks = useMemo(() => doc.tracks, [doc.tracks])
  return (
    <ThemeContext.Provider value={doc.theme}>
      <TracksContext.Provider value={tracks}>
        <AllItemsContext.Provider value={doc.items}>
          <AssetsContext.Provider value={doc.assets}>
            <Layers tracks={tracks} />
            {doc.watermark ? <Watermark /> : null}
          </AssetsContext.Provider>
        </AllItemsContext.Provider>
      </TracksContext.Provider>
    </ThemeContext.Provider>
  )
}

export { TIMELINE_COMPOSITION_ID } from './compositionId'

/** Length, size and rate come from the document, through the one frame policy. */
export const calculateTimelineMetadata: CalculateMetadataFunction<
  TimelineDoc
> = ({ props }) => ({
  durationInFrames: Math.max(
    1,
    docDurationInFrames(Object.values(props.items)),
  ),
  fps: props.fps,
  width: props.compositionWidth,
  height: props.compositionHeight,
})
